import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { EyeOff, Eye, Trash2, RefreshCw, Search } from "lucide-react";
import {
  adminListCommunityPosts,
  adminHidePost,
  adminUnhidePost,
  adminDeletePost,
} from "../../lib/communityApi";
import { formatSgDateTime } from "../../lib/format";
import { HTTP_UNAUTHORIZED } from "../../constants/http";

const AdminCommunityPosts = ({ adminKey, onUnauthorized }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    if (!adminKey) return;
    setLoading(true);
    try {
      const data = await adminListCommunityPosts(adminKey, {
        include_hidden: true,
        limit: 300,
      });
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === HTTP_UNAUTHORIZED) {
        toast.error("Session expired. Please sign in again.");
        if (typeof onUnauthorized === "function") onUnauthorized();
      } else {
        console.error("adminListCommunityPosts failed:", err);
        toast.error("Could not load community posts.");
      }
    } finally {
      setLoading(false);
    }
  }, [adminKey, onUnauthorized]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((it) => {
      if (filter === "visible" && it.hidden) return false;
      if (filter === "hidden" && !it.hidden) return false;
      if (!q) return true;
      const author = (it.author?.first_name || "").toLowerCase();
      const email = (it.author?.email || "").toLowerCase();
      const content = (it.content || "").toLowerCase();
      return author.includes(q) || email.includes(q) || content.includes(q);
    });
  }, [items, query, filter]);

  const handleHide = async (post) => {
    const reason = window.prompt(
      "Hide this post. Optional reason (visible only in admin):",
      ""
    );
    if (reason === null) return;
    try {
      await adminHidePost(adminKey, post.id, reason || undefined);
      setItems((prev) =>
        prev.map((p) =>
          p.id === post.id
            ? { ...p, hidden: true, hidden_reason: reason || "removed by moderator" }
            : p
        )
      );
      toast.success("Post hidden.");
    } catch (err) {
      console.error("adminHidePost failed:", err);
      toast.error("Could not hide post.");
    }
  };

  const handleUnhide = async (post) => {
    try {
      await adminUnhidePost(adminKey, post.id);
      setItems((prev) =>
        prev.map((p) =>
          p.id === post.id ? { ...p, hidden: false, hidden_reason: null } : p
        )
      );
      toast.success("Post visible again.");
    } catch (err) {
      console.error("adminUnhidePost failed:", err);
      toast.error("Could not unhide post.");
    }
  };

  const handleDelete = async (post) => {
    const yes = window.confirm(
      `Permanently delete this post by ${post?.author?.first_name || "someone"}? This cannot be undone.`
    );
    if (!yes) return;
    try {
      await adminDeletePost(adminKey, post.id);
      setItems((prev) => prev.filter((p) => p.id !== post.id));
      toast.success("Post deleted.");
    } catch (err) {
      console.error("adminDeletePost failed:", err);
      toast.error("Could not delete post.");
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9aa6b2]" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by author, email, or content…"
              className="ht-input w-full rounded-md border border-[#dbe3eb] bg-white pl-10 pr-3 py-2.5 text-sm placeholder:text-[#9aa6b2]"
            />
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="ht-input rounded-md border border-[#dbe3eb] bg-white px-3 py-2.5 text-sm text-[#2c3e50]"
          >
            <option value="all">All posts</option>
            <option value="visible">Visible</option>
            <option value="hidden">Hidden</option>
          </select>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="ht-btn inline-flex items-center gap-2 rounded-md bg-white border border-[#dbe3eb] hover:border-[#cfe7f8] text-[#3d6486] px-4 py-2.5 text-sm font-semibold disabled:opacity-60"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      <div className="mt-5 space-y-4">
        {loading && (
          <p className="text-sm text-[#7e8a96] text-center py-6">
            Loading community posts…
          </p>
        )}
        {!loading && filtered.length === 0 && (
          <div className="rounded-2xl bg-white border border-[#e6eef5] p-6 text-center shadow-sm text-sm text-[#7e8a96]">
            No posts match your filters.
          </div>
        )}
        {!loading &&
          filtered.map((post) => (
            <article
              key={post.id}
              className={`rounded-2xl border p-5 shadow-sm bg-white ${
                post.hidden
                  ? "border-[#f3d7d7] bg-[#fdf6f6]"
                  : "border-[#e6eef5]"
              }`}
            >
              <header className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#eaf4fb] text-[#3d6486] font-semibold ht-display shrink-0">
                    {(post?.author?.first_name || "?").slice(0, 1).toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    <p className="ht-body font-semibold text-[#2c3e50] truncate">
                      {post?.author?.first_name || "Friend"}{" "}
                      <span className="text-xs text-[#7e8a96] font-normal">
                        · {post?.author?.email}
                      </span>
                    </p>
                    <p className="text-xs text-[#7e8a96]">
                      {formatSgDateTime(post?.created_at)} · {post.topic}
                      {post.hidden && (
                        <span className="ml-2 inline-block rounded-full bg-[#b04040]/10 text-[#b04040] px-2 py-0.5 text-[11px] font-semibold">
                          Hidden
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {post.hidden ? (
                    <button
                      onClick={() => handleUnhide(post)}
                      className="ht-btn inline-flex items-center gap-1.5 rounded-md bg-white border border-[#dbe3eb] hover:border-[#cfe7f8] text-[#3d6486] px-3 py-1.5 text-xs font-semibold"
                    >
                      <Eye className="h-3.5 w-3.5" /> Unhide
                    </button>
                  ) : (
                    <button
                      onClick={() => handleHide(post)}
                      className="ht-btn inline-flex items-center gap-1.5 rounded-md bg-white border border-[#dbe3eb] hover:border-[#cfe7f8] text-[#3d6486] px-3 py-1.5 text-xs font-semibold"
                    >
                      <EyeOff className="h-3.5 w-3.5" /> Hide
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(post)}
                    className="ht-btn inline-flex items-center gap-1.5 rounded-md bg-white border border-[#f3d7d7] hover:bg-[#fdf6f6] text-[#b04040] px-3 py-1.5 text-xs font-semibold"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </button>
                </div>
              </header>
              <p className="mt-3 ht-body text-[#2c3e50] whitespace-pre-wrap leading-relaxed text-sm">
                {post?.content}
              </p>
              <p className="mt-2 text-xs text-[#7e8a96]">
                {post.reply_count} {post.reply_count === 1 ? "reply" : "replies"}
                {post.hidden && post.hidden_reason && (
                  <span className="ml-2 text-[#b04040]">
                    · reason: {post.hidden_reason}
                  </span>
                )}
              </p>
            </article>
          ))}
      </div>
    </div>
  );
};

export default AdminCommunityPosts;
